/* ============================================================
   TOYDROP, toydrop.js
   Motion + brand interactions layered on top of Dawn's wiring.
   Dawn owns cart/variant/checkout logic; this file only adds
   choreography and brand-specific UI (mobile nav, gallery,
   bundle picker, sticky ATC, fly-to-cart).

   Everything is guarded: with JS off or prefers-reduced-motion
   on, the page is complete and usable, motion is a layer.
   Animates transform/opacity only.
   ============================================================ */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var $ = function (sel, ctx) {
    return (ctx || document).querySelector(sel);
  };
  var $$ = function (sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  };
  var SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

  /* ---------------- 1. Logo ball bounce on load ---------------- */

  if (!reduced) {
    var logo = $('.td-header .td-logo');
    if (logo) {
      logo.classList.add('ball-bounce');
      setTimeout(function () {
        logo.classList.remove('ball-bounce');
      }, 700);
    }
  }

  /* ---------------- 2. Choreographed hero load ---------------- */

  function heroLoad() {
    if (reduced) return;
    var hero = $('[data-hero]');
    if (!hero) return;
    /* The unboxing hero (td-unbox.js) owns its own choreography, and its
       contract forbids opacity-0 entrances on the headline/CTA: they must be
       fully visible at first paint. */
    if (hero.hasAttribute('data-unbox')) return;

    var steps = [
      { els: $$('.hero-copy .word', hero), from: 'translateY(120%) rotate(5deg)', at: 0, stagger: 70 },
      { els: $$('.hero-sub', hero), from: 'translateY(26px)', at: 340, stagger: 0 },
      { els: $$('.hero-cta-row > *', hero), from: 'translateY(22px)', at: 440, stagger: 90 },
      { els: $$('.hero-media-card', hero), from: 'scale(0.96) translateY(24px)', at: 220, stagger: 0 }
    ];

    /* badges settle, then the card starts its gentle continuous float */
    var media = $('.hero-media', hero);
    if (media) {
      setTimeout(function () {
        media.classList.add('floating');
      }, 1900);
    }

    steps.forEach(function (step) {
      step.els.forEach(function (el, i) {
        var to = getComputedStyle(el).transform;
        if (to === 'none') to = 'none';
        el.animate(
          [
            { transform: step.from, opacity: 0 },
            { transform: to === 'none' ? 'none' : to, opacity: 1 }
          ],
          {
            duration: 700,
            delay: step.at + i * step.stagger,
            easing: SPRING,
            fill: 'backwards'
          }
        );
      });
    });

    $$('.trust-item').forEach(function (el, i) {
      el.animate([{ transform: 'translateY(26px)', opacity: 0 }, { transform: 'none', opacity: 1 }], {
        duration: 500,
        delay: 1000 + i * 70,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'backwards'
      });
    });
  }

  /* ---------------- 3. Scroll reveals, varied per section ------ */

  /* Exponential ease-out: fast départ, long settle. Reads as weight rather
     than as a timed effect, and is the house curve for everything that
     enters on scroll. Springs stay for press/selection feedback only. */
  var EASE_OUT_EXPO = 'cubic-bezier(0.16, 1, 0.3, 1)';

  /* TRANSFORM AND OPACITY ONLY. These used to animate `filter: blur()` from 4-6px
     as well, which Chrome must rasterise off-screen at every radius: measured
     over two reveals, 5 frames past 34ms with a WORST FRAME OF 351ms, against 0
     long frames and a 18ms max with the blur removed. A third of a second of
     frozen rendering is what read as each section flashing on scroll. The cost
     was not CPU-bound either (throttling 4x made no difference), which is the
     signature of a raster stall rather than script. */
  var REVEALS = {
    up: { transform: 'translateY(38px)', duration: 900 },
    left: { transform: 'translateX(-52px)', duration: 900 },
    right: { transform: 'translateX(52px)', duration: 900 },
    pop: { transform: 'scale(0.9)', duration: 780 },
    tilt: { transform: 'translateY(44px) rotate(2.5deg)', duration: 900 }
  };

  function playReveal(el, type) {
    var spec = REVEALS[type] || REVEALS.up;
    var resting = getComputedStyle(el).transform;
    el.classList.add('revealed');
    var from = { transform: spec.transform, opacity: 0 };
    var to = { transform: resting === 'none' ? 'none' : resting, opacity: 1 };
    el.animate([from, to], {
      duration: spec.duration,
      easing: spec.easing || EASE_OUT_EXPO,
      fill: 'backwards'
    });
  }

  function playStagger(el, withTilt) {
    el.classList.add('revealed');
    Array.prototype.slice.call(el.children).forEach(function (child, i) {
      var resting = getComputedStyle(child).transform;
      var from = withTilt
        ? 'translateY(34px) rotate(' + (i % 2 === 0 ? -2.5 : 2.5) + 'deg) scale(0.96)'
        : 'translateY(36px)';
      child.animate(
        [
          { transform: from, opacity: 0 },
          { transform: resting === 'none' ? 'none' : resting, opacity: 1 }
        ],
        { duration: 820, delay: i * 85, easing: EASE_OUT_EXPO, fill: 'backwards' }
      );
    });
  }

  function initReveals() {
    var targets = $$('[data-reveal]');
    if (!targets.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('revealed');
      });
      return;
    }

    // Hide from JS (never CSS) so a script failure can't leave a blank page.
    targets.forEach(function (el) {
      el.classList.add('js-reveal');
    });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var type = el.getAttribute('data-reveal') || 'up';
          if (type === 'stagger') playStagger(el, false);
          else if (type === 'cards' || type === 'polaroid') playStagger(el, true);
          else playReveal(el, type);
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -12% 0px' }
    );
    targets.forEach(function (el) {
      io.observe(el);
    });

    /* Safety net: nothing stays invisible because an observer never fired.
       Any element still unrevealed after 4s is simply shown. */
    setTimeout(function () {
      targets.forEach(function (el) {
        if (!el.classList.contains('revealed')) el.classList.add('revealed');
      });
    }, 4000);
  }

  /* ---------------- 4. Parallax blobs (scroll-linked) ---------- */

  function initParallax() {
    var els = $$('[data-parallax]');
    var drifters = $$('[data-drift]');
    if ((!els.length && !drifters.length) || reduced) return;
    var ticking = false;
    /* PHONES GET NO SCROLL-LINKED MOTION. On a touch scroll every one of these
       is a transform written from a scroll handler, which on iOS lands a frame
       behind the finger and reads as jitter; and the drift slides the product
       GRID sideways, which on a 2-up phone grid pushes a card off the screen
       edge. Checked live, so a rotated tablet crossing 900px picks it up. */
    var phoneMq = window.matchMedia('(max-width: 900px)');
    var cleared = false;

    function update() {
      if (phoneMq.matches) {
        if (!cleared) {
          els.concat(drifters).forEach(function (el) { el.style.transform = ''; });
          cleared = true;
        }
        ticking = false;
        return;
      }
      cleared = false;
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var pct = parseFloat(el.getAttribute('data-parallax')) || -20;
        var progress = (vh - rect.top) / (vh + rect.height);
        el.style.transform = 'translateY(' + progress * pct + '%)';
      });
      /* subtle scroll-linked horizontal drift (the drop strip) */
      drifters.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > vh + 100) return;
        var px = parseFloat(el.getAttribute('data-drift')) || 20;
        var progress = (vh - rect.top) / (vh + rect.height) - 0.5;
        el.style.transform = 'translateX(' + (-progress * px) + 'px)';
      });
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  /* ---------------- 5. Mobile nav ---------------- */

  var mobileNav = $('[data-mobile-nav]');
  var navLastFocus = null;

  function openMobileNav() {
    if (!mobileNav) return;
    navLastFocus = document.activeElement;
    mobileNav.classList.add('open');
    document.body.classList.add('overflow-hidden');
    $$('.mobile-nav-item', mobileNav).forEach(function (li, i) {
      li.style.transitionDelay = 0.06 + i * 0.055 + 's';
    });
    var closeBtn = $('[data-nav-close]', mobileNav);
    if (closeBtn) closeBtn.focus();
  }

  function closeMobileNav() {
    if (!mobileNav || !mobileNav.classList.contains('open')) return;
    mobileNav.classList.remove('open');
    document.body.classList.remove('overflow-hidden');
    $$('.mobile-nav-item', mobileNav).forEach(function (li) {
      li.style.transitionDelay = '0s';
    });
    if (navLastFocus) navLastFocus.focus();
  }

  $$('[data-nav-open]').forEach(function (b) {
    b.addEventListener('click', openMobileNav);
  });
  $$('[data-nav-close]').forEach(function (b) {
    b.addEventListener('click', closeMobileNav);
  });

  function trapTab(e, container) {
    var els = $$(
      'a[href], button:not([disabled]), input:not([disabled]), select, [tabindex]:not([tabindex="-1"])',
      container
    ).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!els.length) return;
    var first = els[0];
    var last = els[els.length - 1];
    if (!container.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeMobileNav();
      return;
    }
    if (e.key !== 'Tab') return;
    if (mobileNav && mobileNav.classList.contains('open')) trapTab(e, mobileNav);
  });

  /* ---------------- 6. Add-to-cart delight ---------------- */

  function flyToCart(sourceEl) {
    var cartBtn = $('#cart-icon-bubble');
    if (!cartBtn || reduced || !sourceEl) return;

    var img = sourceEl.tagName === 'IMG' ? sourceEl : $('img', sourceEl);
    if (!img) return;

    var from = img.getBoundingClientRect();
    var to = cartBtn.getBoundingClientRect();
    var fly = document.createElement('div');
    fly.className = 'fly-toy';
    fly.setAttribute('aria-hidden', 'true');
    var clone = document.createElement('img');
    clone.src = img.currentSrc || img.src;
    clone.alt = '';
    fly.appendChild(clone);
    fly.style.left = from.left + from.width / 2 - 38 + 'px';
    fly.style.top = from.top + from.height / 2 - 38 + 'px';
    document.body.appendChild(fly);

    var dx = to.left + to.width / 2 - (from.left + from.width / 2);
    var dy = to.top + to.height / 2 - (from.top + from.height / 2);

    var anim = fly.animate(
      [
        { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1 },
        {
          transform:
            'translate(' + dx * 0.5 + 'px,' + (dy * 0.5 - 90) + 'px) scale(0.7) rotate(-14deg)',
          opacity: 1,
          offset: 0.55
        },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0.15) rotate(10deg)', opacity: 0.6 }
      ],
      { duration: 650, easing: 'cubic-bezier(0.5, 0, 0.6, 1)' }
    );
    anim.onfinish = function () {
      fly.remove();
    };
    anim.oncancel = function () {
      fly.remove();
    };
  }

  function confettiAt(el, n) {
    if (reduced || !el) return;
    var r = el.getBoundingClientRect();
    var cx = r.left + r.width / 2;
    var cy = r.top + r.height / 2;
    var colors = ['#FF5E5E', '#FFC93C', '#FF8FC7', '#4CB9FF', '#2FBF71'];
    for (var i = 0; i < (n || 14); i++) {
      var bit = document.createElement('i');
      bit.className = 'confetti-bit';
      bit.setAttribute('aria-hidden', 'true');
      bit.style.background = colors[i % colors.length];
      bit.style.left = cx + 'px';
      bit.style.top = cy + 'px';
      document.body.appendChild(bit);
      var ang = Math.random() * Math.PI * 2;
      var dist = 44 + Math.random() * 66;
      var anim = bit.animate(
        [
          { transform: 'translate(0,0) rotate(0deg) scale(1)', opacity: 1 },
          {
            transform:
              'translate(' +
              Math.cos(ang) * dist +
              'px,' +
              (Math.sin(ang) * dist + 46) +
              'px) rotate(' +
              (Math.random() * 540 - 270) +
              'deg) scale(0.4)',
            opacity: 0
          }
        ],
        { duration: 620 + Math.random() * 350, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
      anim.onfinish = (function (b) {
        return function () {
          b.remove();
        };
      })(bit);
    }
  }

  /* Fire the flight from whichever product image belongs to the submitted
     form. Dawn's product-form handles the actual cart request. */
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
      var scope = form.closest('.td-card') || form.closest('[data-pdp]') || document;
      var source =
        $('.td-card-media img', scope) ||
        $('.gallery-main img.is-active', scope) ||
        $('.gallery-main img', scope) ||
        $('.sticky-atc .sa-thumb img');
      flyToCart(source);
    },
    true
  );

  /* Pop the cart bubble + confetti once Dawn finishes the add */
  document.addEventListener('DOMContentLoaded', function () {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      var bubble = $('.cart-count-bubble-td');
      if (bubble && !reduced) {
        bubble.classList.remove('cart-count-pop');
        void bubble.offsetWidth;
        bubble.classList.add('cart-count-pop');
      }
      var cartBtn = $('#cart-icon-bubble');
      if (cartBtn && !reduced) {
        cartBtn.classList.remove('cart-wiggle');
        void cartBtn.offsetWidth;
        cartBtn.classList.add('cart-wiggle');
      }
      confettiAt(cartBtn, 14);
    });
  });

  /* ---------------- 7. Sticky PDP add-to-cart bar ---------------- */

  var sentinel = $('[data-buybox-sentinel]');
  var stickyBar = $('[data-sticky-atc]');
  if (sentinel && stickyBar && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      function (entries) {
        var below = entries[0].boundingClientRect.top < 0 && !entries[0].isIntersecting;
        stickyBar.classList.toggle('stuck', below);
        document.body.classList.toggle('atc-stuck', below);
      },
      { threshold: 0 }
    ).observe(sentinel);

    /* slide the bar away while the footer is on screen */
    var footer = $('.td-footer') || $('footer');
    if (footer) {
      new IntersectionObserver(
        function (entries) {
          stickyBar.classList.toggle('footer-visible', entries[0].isIntersecting);
        },
        { threshold: 0 }
      ).observe(footer);
    }
  }

  /* ---------------- 7a. Sticky header condense ---------------- */

  /* The nav is sticky in CSS; the ticker simply scrolls away above it, which
     is the condense. Past 200px this adds a shadow and tightens the logo.
     Both are paint/transform only: changing the header's HEIGHT here would
     shorten the document under a reader who has already scrolled, and the
     page would jump. A 40px hysteresis band stops it flickering when someone
     rests exactly on the threshold. */
  function initHeaderCondense() {
    var header = $('.td-header');
    if (!header) return;
    var on = false;
    var ticking = false;

    function update() {
      ticking = false;
      var y = window.scrollY || window.pageYOffset || 0;
      if (!on && y > 200) {
        on = true;
        document.body.classList.add('header-condensed');
      } else if (on && y < 160) {
        on = false;
        document.body.classList.remove('header-condensed');
      }
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  /* -------- 7b. WhatsApp float dodges the things that matter -------- */

  /* The float is fixed bottom-left, which is exactly where the hero CTA, the
     PDP price/buy row and the sticky ATC bar pass on scroll. Anything marked
     data-wa-avoid gets right of way: while its rect (plus a small margin)
     intersects the float's rect, the float fades out and stops taking taps.
     Fading rather than nudging: the avoid targets sweep through the whole
     bottom band on scroll, so there is nowhere stable to nudge TO, and a
     button that hops around is worse than one that briefly yields. */
  function initWaDodge() {
    var wa = $('.wa-float');
    if (!wa) return;
    var targets = $$('[data-wa-avoid]');
    if (!targets.length) return;

    var PAD = 12;
    var ticking = false;

    function update() {
      ticking = false;
      var w = wa.getBoundingClientRect();
      if (!w.width) return;
      var hit = targets.some(function (t) {
        var r = t.getBoundingClientRect();
        if (!r.width && !r.height) return false; /* hidden target */
        return (
          r.left < w.right + PAD &&
          r.right > w.left - PAD &&
          r.top < w.bottom + PAD &&
          r.bottom > w.top - PAD
        );
      });
      wa.classList.toggle('wa-float--dodge', hit);
    }

    function queue() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    update();
  }

  /* ---------------- 8. PDP gallery ---------------- */

  function initGallery() {
    var main = $('[data-gallery-main]');
    if (!main) return;
    var slides = $$('img', main);
    var thumbs = $$('[data-gallery-thumb]');
    var dots = $$('[data-gallery-dots] .gallery-dot');
    if (!slides.length) return;

    var isCarousel = function () {
      return window.matchMedia('(max-width: 900px)').matches;
    };

    function mark(i, scrollThumb) {
      slides.forEach(function (img, j) {
        img.classList.toggle('is-active', j === i);
      });
      thumbs.forEach(function (t, j) {
        t.setAttribute('aria-current', j === i ? 'true' : 'false');
      });
      dots.forEach(function (d, j) {
        d.classList.toggle('is-active', j === i);
      });
      /* keep the active thumb in view within its strip, only on user
         interaction, never on the initial mark (it would scroll the page) */
      if (scrollThumb && thumbs[i]) {
        var strip = thumbs[i].parentElement;
        var target = thumbs[i].offsetLeft - strip.clientWidth / 2 + thumbs[i].clientWidth / 2;
        strip.scrollTo({
          left: Math.max(0, target),
          behavior: reduced ? 'auto' : 'smooth'
        });
      }
    }

    function show(i) {
      mark(i, true);
      if (isCarousel()) {
        /* Mobile: the gallery is a snap carousel, scroll to the slide */
        slides[i].scrollIntoView({
          behavior: reduced ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
        return;
      }
      if (!reduced) {
        slides[i].animate(
          [
            { transform: 'scale(0.9) rotate(-1.5deg)', opacity: 0 },
            { transform: 'none', opacity: 1 }
          ],
          { duration: 380, easing: SPRING }
        );
      }
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        show(i);
      });
    });

    /* Swiping the mobile carousel keeps the thumbnails in sync */
    var scrollTimer = null;
    main.addEventListener(
      'scroll',
      function () {
        if (!isCarousel()) return;
        if (scrollTimer) clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var i = Math.round(main.scrollLeft / main.clientWidth);
          mark(Math.max(0, Math.min(slides.length - 1, i)), true);
        }, 80);
      },
      { passive: true }
    );

    mark(0);
  }

  /* ---------------- 9. Bundle & save picker ---------------- */

  /* Sets the quantity input on the product form. The matching Shopify
     automatic discounts (Buy 2 / Buy 3) apply the saving at checkout. */
  /* Recompute "Add R x more for free delivery" from what is already in the
     cart plus the bundle the shopper has selected. Money formatting is done
     with Intl so it matches the server-rendered ZAR string. */
  function updateFreeHint(lineTotalCents) {
    var hint = $('[data-free-hint]');
    if (!hint || !isFinite(lineTotalCents)) return;
    var mount = $('[data-free-hint-text]', hint);
    if (!mount) return;

    var threshold = parseInt(hint.getAttribute('data-threshold'), 10);
    var cartTotal = parseInt(hint.getAttribute('data-cart-total'), 10) || 0;
    if (!isFinite(threshold)) return;

    var shortfall = threshold - (cartTotal + lineTotalCents);
    if (shortfall > 0) {
      var rands = Math.ceil(shortfall / 100);
      var amount = 'R ' + rands.toLocaleString('en-ZA').replace(/,/g, ' ');
      mount.innerHTML = (hint.getAttribute('data-hint-away') || '').replace('__AMT__', amount);
    } else {
      mount.innerHTML = hint.getAttribute('data-hint-hit') || '';
    }
  }

  function initBundle() {
    var picker = $('[data-bundle-picker]');
    if (!picker) return;
    var options = $$('[data-bundle-option]', picker);

    function setLabel(label, instant) {
      if (!label) return;
      var buttons = $$('button[name="add"] > span, .sticky-atc button[type="submit"]');
      buttons.forEach(function (b) {
        if (b.closest('.td-card')) return;
        if (b.textContent === label) return;
        if (reduced || instant) {
          b.textContent = label;
          return;
        }
        /* quick slide-morph: old label drops out, new one springs in */
        b.animate(
          [{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(60%)', opacity: 0 }],
          { duration: 110, easing: 'ease-in' }
        ).onfinish = function () {
          b.textContent = label;
          b.animate(
            [{ transform: 'translateY(-60%)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }],
            { duration: 240, easing: SPRING }
          );
        };
      });
    }

    function apply(opt, instant) {
      options.forEach(function (o) {
        o.setAttribute('aria-pressed', 'false');
      });
      opt.setAttribute('aria-pressed', 'true');

      var qty = opt.getAttribute('data-qty');
      var formId = picker.getAttribute('data-form-id');
      /* The quantity field is form-associated rather than nested, so find it
         by its marker first and fall back to the form's own stepper. */
      var input =
        $('[data-qty-input]') ||
        (formId ? document.querySelector('form#' + formId + ' input[name="quantity"]') : null) ||
        $('input[name="quantity"]');
      if (input) {
        input.value = qty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* The buy button echoes the choice, so picking and adding feel like one
         motion ("Add 2 boxes · R349"). Sticky bar mirrors it. */
      setLabel(opt.getAttribute('data-atc-label'), instant);

      /* the free-delivery hint follows the selection: a bigger bundle can
         close the gap, which is the honest version of the upsell */
      updateFreeHint(parseInt(opt.getAttribute('data-line-total'), 10));

      if (!reduced && !instant) {
        opt.animate(
          [
            { transform: 'scale(1)' },
            { transform: 'scale(0.96)' },
            { transform: 'scale(1.02)' },
            { transform: 'scale(1)' }
          ],
          { duration: 380, easing: SPRING }
        );
      }
    }

    options.forEach(function (opt) {
      opt.addEventListener('click', function () {
        apply(opt, false);
      });
    });

    /* boot: sync the ATC button/qty input to the pre-selected option
       (Party Pair by default) without any animation */
    var pressed = options.filter(function (o) {
      return o.getAttribute('aria-pressed') === 'true';
    })[0];
    if (pressed) apply(pressed, true);
  }

  /* ---------------- 10. Hero video (poster-first, idle prefetch) -- */

  /* The markup ships only the poster image (the LCP element). The video
     fetch is deferred to idle so it never competes with the poster, then the
     file preloads fully OFF-DOM and only mounts once it can play through, the loop starts instantly, no visible buffering. Reduced motion / JS off
     fall back to the poster. */
  function initHeroVideo() {
    var card = $('[data-hero-media]');
    if (!card || reduced) return;
    var src = card.getAttribute('data-video-src');
    if (!src) return;

    var started = false;
    function prefetch() {
      if (started || card.querySelector('video')) return;
      started = true;
      var v = document.createElement('video');
      v.muted = true;
      v.setAttribute('muted', '');
      v.loop = true;
      v.setAttribute('playsinline', '');
      v.playsInline = true;
      v.preload = 'auto';
      v.setAttribute('aria-hidden', 'true');
      v.tabIndex = -1;
      var poster = card.getAttribute('data-video-poster');
      if (poster) v.poster = poster;

      var mounted = false;
      var mount = function () {
        if (mounted || card.querySelector('video')) return;
        mounted = true;
        card.appendChild(v);
        var play = v.play();
        if (play && play.catch) {
          play.catch(function () {
            v.remove();
          });
        }
      };
      v.addEventListener('canplaythrough', mount, { once: true });
      /* some mobile browsers withhold canplaythrough for autoplay video, mount shortly after first data rather than never */
      v.addEventListener(
        'loadeddata',
        function () {
          setTimeout(mount, 1200);
        },
        { once: true }
      );
      v.src = src;
      v.load();
    }

    var whenIdle = function () {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(prefetch, { timeout: 3000 });
      } else {
        setTimeout(prefetch, 500);
      }
    };
    if (document.readyState === 'complete') {
      whenIdle();
    } else {
      window.addEventListener('load', whenIdle, { once: true });
    }
  }

  /* ------- 10a2. Scroll-gated videos (FYP strip, satisfying loop) -- */

  /* data-td-video cards get their <video> only when they near the viewport,
     play while on screen and pause the moment they leave. */
  function initTdVideos() {
    var cards = $$('[data-td-video]');
    if (!cards.length || reduced || !('IntersectionObserver' in window)) return;

    function attach(card) {
      var existing = card.querySelector('video');
      if (existing) return existing;
      var v = document.createElement('video');
      v.muted = true;
      v.setAttribute('muted', '');
      v.loop = true;
      v.setAttribute('playsinline', '');
      v.playsInline = true;
      v.preload = 'auto';
      v.setAttribute('aria-hidden', 'true');
      v.tabIndex = -1;
      var poster = card.getAttribute('data-poster');
      if (poster) v.poster = poster;
      v.src = card.getAttribute('data-src');
      card.appendChild(v);
      return v;
    }

    var warm = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          attach(entry.target);
          warm.unobserve(entry.target);
        });
      },
      { rootMargin: '320px 0px' }
    );

    var player = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target.querySelector('video');
          if (entry.isIntersecting) {
            if (!v) v = attach(entry.target);
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else if (v) {
            v.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    cards.forEach(function (card) {
      warm.observe(card);
      player.observe(card);
    });
  }

  /* ---------------- 10a3. Live drop countdown --------------------- */

  /* Milliseconds until the next Friday 09:00 Africa/Johannesburg (UTC+2
     year-round, no DST). Pure so it can be tested with a faked clock; the
     instant Friday 09:00 passes it rolls to the following week. */
  function tdNextDropMs(nowMs) {
    var SAST = 2 * 3600000;
    var local = nowMs + SAST;
    var d = new Date(local);
    var target = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 9, 0, 0, 0);
    var daysAhead = (5 - d.getUTCDay() + 7) % 7;
    target += daysAhead * 86400000;
    if (target <= local) target += 7 * 86400000;
    return target - local;
  }
  window.tdNextDropMs = tdNextDropMs;

  function initCountdown() {
    var chip = $('[data-drop-countdown]');
    if (!chip) return;
    var timer = $('[data-countdown-timer]', chip);
    if (!timer) return;

    function fmt(ms) {
      var mins = Math.ceil(ms / 60000);
      var d = Math.floor(mins / 1440);
      mins -= d * 1440;
      var h = Math.floor(mins / 60);
      var m = mins % 60;
      if (d > 0) return d + 'd ' + h + 'h ' + m + 'm';
      if (h > 0) return h + 'h ' + m + 'm';
      return m + 'm';
    }

    function tick() {
      timer.textContent = fmt(tdNextDropMs(Date.now()));
      timer.hidden = false;
    }
    tick();
    setInterval(tick, 15000);
  }

  /* ---------------- 10a4. Yellow highlight sweep ------------------- */

  /* .hl is fully visible by default; JS collapses it pre-paint and wipes it
     back in on scroll-into-view. Reduced motion / no JS: just visible. */
  function initHighlights() {
    /* the unbox hero's sweep is timed by its scroll timeline, not by an
       IntersectionObserver (which would fire it immediately, hero in view) */
    var els = $$('.hl').filter(function (el) {
      return !el.hasAttribute('data-unbox-hl');
    });
    if (!els.length || reduced || !('IntersectionObserver' in window)) return;

    els.forEach(function (el) {
      el.classList.add('hl-anim');
    });
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('hl-on');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    els.forEach(function (el) {
      io.observe(el);
    });
    /* safety net, nothing stays unhighlighted forever */
    setTimeout(function () {
      els.forEach(function (el) {
        el.classList.add('hl-on');
      });
    }, 4000);
  }

  /* ---------------- 10a5. Header dropdown (details) ---------------- */

  function initNavDrops() {
    var drops = $$('[data-nav-drop]');
    if (!drops.length) return;

    var hoverable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    drops.forEach(function (drop) {
      if (hoverable) {
        var closeTimer;
        drop.addEventListener('mouseenter', function () {
          clearTimeout(closeTimer);
          drop.open = true;
        });
        drop.addEventListener('mouseleave', function () {
          closeTimer = setTimeout(function () {
            drop.open = false;
          }, 160);
        });
      }
      /* keyboard: close when focus leaves the dropdown */
      drop.addEventListener('focusout', function () {
        requestAnimationFrame(function () {
          if (!drop.contains(document.activeElement)) drop.open = false;
        });
      });
    });

    document.addEventListener('click', function (e) {
      drops.forEach(function (drop) {
        if (drop.open && !drop.contains(e.target)) drop.open = false;
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      drops.forEach(function (drop) {
        if (!drop.open) return;
        var hadFocus = drop.contains(document.activeElement);
        drop.open = false;
        if (hadFocus) {
          var s = $('summary', drop);
          if (s) s.focus();
        }
      });
    });
  }

  /* ---------------- 10b. Stars fill left-to-right on entry -------- */

  function initStars() {
    var stars = $$('.stars--animate');
    if (!stars.length) return;
    if (reduced || !('IntersectionObserver' in window)) return;

    stars.forEach(function (el) {
      el.classList.add('js-stars');
    });
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('lit');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    stars.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------------- 10c. Stats count up on scroll-into-view ------- */

  function initCountUps() {
    var nums = $$('[data-count-up]');
    if (!nums.length) return;
    if (reduced || !('IntersectionObserver' in window)) return;

    function animate(el) {
      var raw = el.textContent.trim();
      /* keep any non-numeric dressing ("R", "+", spaces as thousands) */
      var match = raw.match(/[\d\s., ]*\d/);
      if (!match) return;
      var numText = match[0];
      var prefix = raw.slice(0, match.index);
      var suffix = raw.slice(match.index + numText.length);
      var target = parseFloat(numText.replace(/[\s ,]/g, ''));
      if (!isFinite(target)) return;
      var sep = numText.indexOf(',') > -1 ? ',' : numText.match(/[\s ]/) ? ' ' : '';

      var start = null;
      var dur = 1300;
      function frame(t) {
        if (start === null) start = t;
        var p = Math.min(1, (t - start) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        var val = Math.round(target * eased);
        var out = String(val);
        if (sep) out = out.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
        el.textContent = prefix + out + suffix;
        if (p < 1) requestAnimationFrame(frame);
        else el.textContent = raw;
      }
      requestAnimationFrame(frame);
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          animate(entry.target);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    nums.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------------- 10d. Dotted courier path self-draws ----------- */

  function initStepsPath() {
    var masks = $$('[data-steps-mask]');
    if (!masks.length) return;
    if (reduced || !('IntersectionObserver' in window)) return;

    masks.forEach(function (mask) {
      var len;
      try {
        len = mask.getTotalLength();
      } catch (e) {
        return;
      }
      mask.style.strokeDasharray = len;
      mask.style.strokeDashoffset = len;
      var io = new IntersectionObserver(
        function (entries) {
          if (!entries[0].isIntersecting) return;
          mask.animate(
            [{ strokeDashoffset: len }, { strokeDashoffset: 0 }],
            { duration: 1500, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
          );
          mask.style.strokeDashoffset = 0;
          io.disconnect();
        },
        { rootMargin: '0px 0px -18% 0px' }
      );
      io.observe(mask.closest('.steps-wrap') || mask);
    });
  }

  /* ---------------- 10d2. Courier route wipes in ------------------ */

  /* The dotted drop path is revealed with a clip-path wipe rather than the
     stroke-dashoffset draw used by the steps path: that technique rewrites
     stroke-dasharray, which would turn this dotted route into a solid line. */
  function initDropPaths() {
    var paths = $$('[data-path-draw]');
    if (!paths.length) return;
    if (reduced || !('IntersectionObserver' in window)) return;

    paths.forEach(function (p) {
      p.classList.add('js-path');
    });
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('drawn');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -15% 0px' }
    );
    paths.forEach(function (p) {
      io.observe(p);
    });
    /* safety net: never leave a segment permanently hidden */
    setTimeout(function () {
      paths.forEach(function (p) {
        p.classList.add('drawn');
      });
    }, 5000);
  }

  /* ---------------- 10e. Buy-box review summary ------------------- */

  /* Computed data comes from the reviews section's own blocks; this only
     copies it next to the product title: stars + "4.7 · 10 reviews" + the
     reviewer avatar cluster + the rotating mini-testimonial. No blocks →
     none of it renders. */
  function cloneStars(rating) {
    var template = $('#toydrop-reviews .stars');
    if (!template) return null;
    var clone = template.cloneNode(true);
    clone.classList.remove('stars--animate', 'js-stars', 'lit');
    clone.style.setProperty('--star-w', (rating / 5) * 100 + '%');
    clone.removeAttribute('role');
    clone.removeAttribute('aria-label');
    clone.setAttribute('aria-hidden', 'true');
    return clone;
  }

  function initReviewSummary() {
    var feed = $('[data-td-review-summary]');
    if (!feed) return;

    var data;
    try {
      data = JSON.parse(feed.textContent);
    } catch (e) {
      return;
    }
    if (!data || !data.count) return;

    var anchor = $('[data-buybox-rating]');
    if (anchor) {
      var starsMount = $('[data-buybox-stars]', anchor);
      var textMount = $('[data-buybox-rating-text]', anchor);
      var stars = cloneStars(data.average);
      if (starsMount && stars) starsMount.appendChild(stars);
      if (textMount) {
        /* the on-page review blocks are a sample, so the raw count is never
           printed; the merchant's own label carries the volume claim */
        var labelFeed = $('[data-td-rating-label]');
        var label = '';
        if (labelFeed) {
          try {
            label = JSON.parse(labelFeed.textContent) || '';
          } catch (e) {
            label = '';
          }
        }
        textMount.textContent = label ? data.average + ' · ' + label : String(data.average);
      }
      /* overlapping reviewer avatars, faces before copy */
      var avMount = $('[data-buybox-avatars]', anchor);
      if (avMount && data.avatars && data.avatars.length) {
        data.avatars.slice(0, 5).forEach(function (url) {
          var img = document.createElement('img');
          img.src = url;
          img.alt = '';
          img.width = 26;
          img.height = 26;
          img.loading = 'lazy';
          avMount.appendChild(img);
        });
      }
      anchor.hidden = false;
    }

    /* Hero rating chip: same feed, its own mount. The chip is server-rendered
       with its label and flag, so first paint shows real words; this fills in
       only the faces and the stars around them. NO numeric average here, by
       design (2026-08-22): the number stays on the PDP and in the reviews
       section, the hero label carries the claim. With no feed on the page the
       chip simply keeps its label line. */
    var hero = $('[data-hero-rating]');
    if (hero) {
      var hrStars = $('[data-hr-stars]', hero);
      var hrAv = $('[data-hr-avatars]', hero);
      var hrClone = cloneStars(data.average);
      if (hrStars && hrClone) hrStars.appendChild(hrClone);
      if (hrAv && data.avatars && data.avatars.length) {
        data.avatars.slice(0, 3).forEach(function (url) {
          var img = document.createElement('img');
          img.src = url;
          img.alt = '';
          img.width = 24;
          img.height = 24;
          img.loading = 'lazy';
          hrAv.appendChild(img);
        });
      }
    }

    initBuyboxQuote(data);
  }

  /* rotating mini-testimonial: pinned review leads, then block order */
  function initBuyboxQuote(data) {
    var box = $('[data-buybox-quote]');
    if (!box || !data.quotes || !data.quotes.length) return;

    var quotes = data.quotes.slice().sort(function (a, b) {
      return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
    });

    var textEl = $('[data-bq-text]', box);
    var nameEl = $('[data-bq-name]', box);
    var starsEl = $('[data-bq-stars]', box);

    function truncate(s) {
      if (s.length <= 160) return s;
      return s.slice(0, 157).replace(/\s+\S*$/, '') + '…';
    }

    function show(idx) {
      var q = quotes[idx];
      if (textEl) textEl.textContent = '“' + truncate(q.body) + '”';
      if (nameEl) nameEl.textContent = q.name;
      if (starsEl) {
        starsEl.innerHTML = '';
        var stars = cloneStars(q.rating);
        if (stars) starsEl.appendChild(stars);
      }
      box.hidden = false;
      if (!reduced) {
        box.classList.remove('bq-swap');
        void box.offsetWidth;
        box.classList.add('bq-swap');
      }
    }

    var i = 0;
    show(0);
    if (quotes.length > 1 && !reduced) {
      setInterval(function () {
        i = (i + 1) % quotes.length;
        show(i);
      }, 6000);
    }
  }

  /* ---------------- 10f. Review photo lightbox -------------------- */

  function initLightbox() {
    var dialog = $('[data-td-lightbox]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    var img = $('[data-lightbox-img]', dialog);

    $$('[data-lightbox-src]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (img) img.src = btn.getAttribute('data-lightbox-src');
        dialog.showModal();
      });
    });
    $$('[data-lightbox-close]', dialog).forEach(function (btn) {
      btn.addEventListener('click', function () {
        dialog.close();
      });
    });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
    });
  }

  /* ---------------- 10g. Ticker ----------------------------------- */

  /* The marquee is pure CSS (seamless four-run track). Nothing to drive
     here; this only eases the bar in on first paint. */
  function initTicker() {
    if (reduced) return;
    var ticker = $('.ticker');
    if (!ticker) return;
    ticker.animate(
      [
        { transform: 'translateY(-100%)', opacity: 0 },
        { transform: 'none', opacity: 1 }
      ],
      { duration: 600, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'backwards' }
    );
  }

  /* ---------------- 10h. Brand sort control ------------------------ */

  /* The native <select> stays in the DOM and keeps owning the value; this
     paints a brand listbox over it so the browser's default dropdown never
     shows. Without JS the native control is fully visible and functional. */
  function initSort() {
    $$('[data-td-sort]').forEach(function (wrap) {
      var select = $('[data-td-sort-select]', wrap);
      if (!select || wrap.querySelector('.td-sort-button')) return;

      wrap.classList.add('js-sort');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'td-sort-button';
      btn.setAttribute('aria-haspopup', 'listbox');
      btn.setAttribute('aria-expanded', 'false');
      var labelEl = $('.td-sort-label', wrap);
      if (labelEl && labelEl.id === '') labelEl.id = 'td-sort-label';
      if (labelEl) btn.setAttribute('aria-labelledby', labelEl.id + ' ' + 'td-sort-value');

      var value = document.createElement('span');
      value.className = 'td-sort-value';
      value.id = 'td-sort-value';
      value.textContent = select.options[select.selectedIndex].text;
      btn.appendChild(value);
      btn.insertAdjacentHTML(
        'beforeend',
        '<svg class="td-sort-caret" viewBox="0 0 12 8" aria-hidden="true" focusable="false"><path d="M1.5 1.5 L6 6 L10.5 1.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      );

      var list = document.createElement('ul');
      list.className = 'td-sort-list';
      list.setAttribute('role', 'listbox');
      list.hidden = true;

      var options = Array.prototype.map.call(select.options, function (opt, i) {
        var li = document.createElement('li');
        li.className = 'td-sort-option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '-1');
        li.setAttribute('aria-selected', i === select.selectedIndex ? 'true' : 'false');
        li.textContent = opt.text;
        li.addEventListener('click', function () {
          select.selectedIndex = i;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        list.appendChild(li);
        return li;
      });

      function close(focusBtn) {
        list.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
        if (focusBtn) btn.focus();
      }
      function open() {
        list.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
        var sel = options[select.selectedIndex] || options[0];
        if (sel) sel.focus();
      }

      btn.addEventListener('click', function () {
        if (list.hidden) open();
        else close(true);
      });

      list.addEventListener('keydown', function (e) {
        var i = options.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          var next = e.key === 'ArrowDown' ? i + 1 : i - 1;
          if (next < 0) next = options.length - 1;
          if (next >= options.length) next = 0;
          options[next].focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          document.activeElement.click();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          close(true);
        } else if (e.key === 'Tab') {
          close(false);
        }
      });

      document.addEventListener('click', function (e) {
        if (!wrap.contains(e.target)) close(false);
      });

      wrap.appendChild(btn);
      wrap.appendChild(list);
    });
  }

  /* ---------------- 10f. Reviews carousel controls ---------------- */

  /* The rail is a native scroll-snap strip (works with JS off); this adds
     arrows and one dot per card so it reads as deliberate rather than a
     clipped marquee. The active dot follows whichever card's snap origin is
     nearest the rail's left edge. */
  function initReviewsRail() {
    /* every rail on the page, not just the first: the reviews section can be
       placed on more than one template */
    $$('[data-reviews-rail]').forEach(function (rail) {
      var nav = rail.parentNode ? $('[data-reviews-nav]', rail.parentNode) : null;
      if (nav) setupReviewsRail(rail, nav);
    });
  }

  function setupReviewsRail(rail, nav) {
    var track = $('[data-reviews-track]', rail);
    var cards = track ? $$('.td-review-card', track) : [];
    if (cards.length < 2) return;

    var prev = $('[data-reviews-prev]', nav);
    var next = $('[data-reviews-next]', nav);
    var dotsHost = $('[data-reviews-dots]', nav);
    var goTo = dotsHost.getAttribute('data-label-goto');
    var behavior = reduced ? 'auto' : 'smooth';

    var dots = cards.map(function (card, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'reviews-dot';
      b.setAttribute(
        'aria-label',
        (goTo || 'Go to review [index]').replace('[index]', i + 1)
      );
      b.addEventListener('click', function () {
        rail.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: behavior });
      });
      dotsHost.appendChild(b);
      return b;
    });

    function activeIndex() {
      var x = rail.scrollLeft;
      var best = 0;
      var bestD = Infinity;
      cards.forEach(function (card, i) {
        var d = Math.abs(card.offsetLeft - track.offsetLeft - x);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      return best;
    }

    function sync() {
      var i = activeIndex();
      dots.forEach(function (d, j) {
        d.classList.toggle('is-active', j === i);
      });
      var max = rail.scrollWidth - rail.clientWidth;
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= max - 2;
    }

    function step(dir) {
      var i = activeIndex() + dir;
      i = Math.max(0, Math.min(cards.length - 1, i));
      rail.scrollTo({ left: cards[i].offsetLeft - track.offsetLeft, behavior: behavior });
    }

    prev.addEventListener('click', function () {
      step(-1);
    });
    next.addEventListener('click', function () {
      step(1);
    });

    var ticking = false;
    rail.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          sync();
        });
      },
      { passive: true }
    );
    window.addEventListener('resize', sync, { passive: true });

    nav.hidden = false;
    sync();

    /* ---- continuous right-to-left drift --------------------------------- */
    /* The rail used to be an auto-drifting marquee. That was replaced because it
       clipped the left-hand card behind a fade and gave the reader no way to
       steer. The motion is wanted back, so it returns WITHOUT either fault:
       there is no fade, the arrows/dots/swipe all still work, and the moment the
       reader steers, the drift stops and stays stopped while they are engaged.
       After ~6s without any interaction it resumes; hover and focus still pause
       it for as long as they last (WCAG 2.2.2).

       Implemented by nudging scrollLeft rather than translating a track, so
       snapping, swiping, keyboard scrolling and the existing arrows keep working
       untouched. The card set is duplicated (aria-hidden, so it is announced
       once) and the scroll position wraps by exactly one set width, which is
       invisible because the content either side of the seam is identical. */
    if (reduced) return;

    var setWidth = 0;
    function measureSet() {
      setWidth = 0;
      cards.forEach(function (c) {
        setWidth = Math.max(setWidth, c.offsetLeft + c.offsetWidth - cards[0].offsetLeft);
      });
      setWidth += parseFloat(getComputedStyle(track).gap) || 0;
    }
    measureSet();
    /* nothing to drift if the cards already fit: it would just pan empty space */
    if (setWidth <= rail.clientWidth + 24) return;

    /* enough copies to cover the rail twice over, so the seam is never on screen */
    var copies = Math.ceil((rail.clientWidth * 2) / setWidth) + 1;
    for (var c = 0; c < copies; c++) {
      cards.forEach(function (card) {
        var clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        $$('a, button, input', clone).forEach(function (f) { f.tabIndex = -1; });
        track.appendChild(clone);
      });
    }

    var SPEED = 26;          /* px per second: a drift, not a carousel */
    var IDLE_RESUME = 2000;  /* ms without interaction before drift resumes.
                                The resume timer adds 200ms on top, which must
                                stay longer than the arrows' native smooth
                                scroll (a few hundred ms) or the resume re-reads
                                scrollLeft mid-flight; verified by probe. */
    var steeredAt = 0;       /* performance.now() of the last deliberate steer */
    var resumeTimer = 0;
    var hovered = false;
    var visible = false;
    var lastT = 0;
    var raf = 0;
    /* Accumulate the position in a VARIABLE and assign that. At 26px/s a frame
       advances scrollLeft by ~0.43px, and reading it back rounds the fraction
       away, so `scrollLeft += delta` loses every increment and the rail never
       moves at all. */
    var pos = rail.scrollLeft;

    function drifting() {
      if (steeredAt && performance.now() - steeredAt < IDLE_RESUME) return false;
      return !hovered && visible && !document.hidden;
    }

    /* Bring the reader back into the REAL set, so the arrows and dots keep
       addressing the eight actual reviews rather than a clone. Imperceptible:
       the content either side of the wrap is identical. Runs on every steer
       AND every resume, because a resumable drift can wrap more than once. */
    function normalize() {
      if (setWidth > 0 && rail.scrollLeft >= setWidth) {
        rail.scrollLeft = rail.scrollLeft % setWidth;
      }
    }

    function frame(t) {
      raf = 0;
      if (!drifting()) {
        rail.classList.remove('is-drifting');
        lastT = 0;
        return;
      }
      rail.classList.add('is-drifting');
      if (lastT) {
        pos += (SPEED * (t - lastT)) / 1000;
        if (pos >= setWidth) pos -= setWidth;
        rail.scrollLeft = pos;
      }
      lastT = t;
      raf = requestAnimationFrame(frame);
    }

    function kick() {
      if (raf || !drifting()) return;
      pos = rail.scrollLeft;      /* the reader may have moved it meanwhile */
      raf = requestAnimationFrame(frame);
    }

    function takeOver() {
      steeredAt = performance.now();
      rail.classList.remove('is-drifting');
      normalize();
      /* Re-arm the idle resume on EVERY steer, so a reader who keeps
         interacting is never fought. The +200ms clears the tail of the
         arrows' smooth scroll before pos is re-read from scrollLeft. */
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        normalize();
        kick();
      }, IDLE_RESUME + 200);
    }

    /* pause while the pointer or focus is on the rail, WCAG 2.2.2 */
    rail.addEventListener('pointerenter', function () { hovered = true; });
    rail.addEventListener('pointerleave', function () { hovered = false; kick(); });
    rail.addEventListener('focusin', function () { hovered = true; });
    rail.addEventListener('focusout', function () { hovered = false; kick(); });
    /* any deliberate steer pauses the drift; idle resumes it */
    ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach(function (ev) {
      rail.addEventListener(ev, takeOver, { passive: true });
    });
    nav.addEventListener('click', takeOver);
    document.addEventListener('visibilitychange', kick);
    window.addEventListener('resize', function () {
      measureSet();
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          visible = e.isIntersecting;
          kick();
        });
      }, { rootMargin: '120px 0px' }).observe(rail);
    } else {
      visible = true;
      kick();
    }
  }

  /* ---------------- 11. Newsletter success confetti ---------------- */

  function initNewsletter() {
    var success = $('[data-newsletter-success]');
    if (success && !reduced) confettiAt(success, 16);
  }

  /* ---------------- 11b. Welcome offer popup ---------------------- */

  /* Email capture into the native customer list, then the code with an
     auto-apply link. Native <dialog> (the lightbox precedent), so Escape,
     the focus trap and the top layer are the platform's. */
  function initWelcome() {
    var dialog = $('[data-td-welcome]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    var KEY = 'td-welcome';
    var NATIVE_FLAG = 'td-welcome-native';
    var DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

    function readState() {
      try {
        return JSON.parse(localStorage.getItem(KEY) || 'null');
      } catch (e) {
        return null;
      }
    }
    function writeState(s) {
      try {
        localStorage.setItem(KEY, JSON.stringify(s));
      } catch (e) {
        /* private mode: the popup simply fires per pageview */
      }
    }

    var state = readState();
    var body = $('[data-welcome-body]', dialog);
    var done = $('[data-welcome-done]', dialog);
    var form = $('form', dialog);
    var opened = false;

    function showSuccessPanel() {
      if (body) body.hidden = true;
      if (done) {
        done.hidden = false;
        var cta = $('[data-welcome-cta]', done);
        if (cta) cta.focus();
        confettiAt($('[data-welcome-code]', done), 16);
      }
    }

    /* A native (captcha fallback) round trip re-rendered the section in its
       success state. Record the subscription; re-open the dialog only when
       THIS popup started the post (session flag), so a signup through the
       Drop List section never pops a modal, it just retires the popup. */
    if (dialog.hasAttribute('data-welcome-posted')) {
      writeState({ kind: 'subscribed', at: Date.now() });
      var cameFromPopup = false;
      try {
        cameFromPopup = sessionStorage.getItem(NATIVE_FLAG) === '1';
        sessionStorage.removeItem(NATIVE_FLAG);
      } catch (e) {
        cameFromPopup = false;
      }
      if (cameFromPopup) {
        dialog.showModal();
        showSuccessPanel();
        opened = true;
      }
    }

    /* ---- suppression, in order ---- */
    if (!opened) {
      if (state && state.kind === 'subscribed') return;
      if (state && state.kind === 'dismissed' && Date.now() - state.at < DISMISS_MS) return;
      var main = document.querySelector('main[data-template]');
      var template = (main && main.dataset.template) || '';
      if (template.indexOf('cart') === 0) return;
      /* a /discount/ link already set a code; do not beg for an email to
         hand over one they have */
      if (document.cookie.indexOf('discount_code=') !== -1) return;
    }

    function blocked() {
      /* never over the open cart drawer, and the consent card (compliance
         UI) gets first claim on the reader's attention */
      if (document.querySelector('cart-drawer.active')) return true;
      if (document.querySelector('[data-td-consent]:not([hidden])')) return true;
      return false;
    }

    var retries = 0;
    function tryOpen() {
      if (opened) return;
      if (blocked()) {
        if (retries < 2) {
          retries += 1;
          setTimeout(tryOpen, 15000);
        }
        /* giving up writes no record: the next pageview gets its chance */
        return;
      }
      opened = true;
      dialog.showModal();
    }

    if (!opened) {
      if (window.matchMedia('(max-width: 900px)').matches) {
        setTimeout(tryOpen, 10000);
      } else {
        var onOut = function (e) {
          if (e.relatedTarget || e.clientY > 0) return;
          document.removeEventListener('mouseout', onOut);
          tryOpen();
        };
        document.addEventListener('mouseout', onOut);
      }
    }

    /* Native showModal makes the page inert but still lets Tab escape to the
       browser chrome once per cycle; the shared trapTab wrap keeps the cycle
       inside the dialog, which is the focus-trap contract. */
    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'Tab') trapTab(e, dialog);
    });

    /* ---- dismissal: X, backdrop and Escape all end at the close event ---- */
    $$('[data-welcome-close]', dialog).forEach(function (btn) {
      btn.addEventListener('click', function () {
        dialog.close();
      });
    });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', function () {
      var s = readState();
      if (s && s.kind === 'subscribed') return;
      writeState({ kind: 'dismissed', at: Date.now() });
    });

    /* ---- submit: fetch to the same /contact endpoint the Drop List form
       posts to. Shopify's bot challenge cannot be solved in a fetch, so on
       anything that is not a clean 2xx the form falls back to a native
       POST; the session flag lets the reloaded page re-open the success
       state. A 2xx that silently failed server-side would show the code
       without a signup, accepted as the lesser failure. ---- */
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var submit = $('[data-welcome-submit]', form);
        if (submit) submit.disabled = true;
        function nativeFallback() {
          try {
            sessionStorage.setItem(NATIVE_FLAG, '1');
          } catch (err) {
            /* storage blocked: the reload will record subscribed anyway */
          }
          form.submit();
        }
        fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'text/html' }
        })
          .then(function (resp) {
            if (resp.ok && resp.url.indexOf('/challenge') === -1) {
              writeState({ kind: 'subscribed', at: Date.now() });
              showSuccessPanel();
            } else {
              nativeFallback();
            }
          })
          .catch(nativeFallback);
      });
    }
  }

  /* ---------------- 11c. Cookie consent card ---------------------- */

  /* Corner card wired to Shopify's Customer Privacy API, so the choice
     actually gates tracking instead of decorating it: Accept and Decline both
     call setTrackingConsent. It shows on a shopper's FIRST visit, i.e. while
     no choice is recorded either in the API or in our own flag.

     IT DOES NOT ASK shouldShowBanner() ANY MORE, deliberately. That call
     answers "does Shopify's own banner legally have to appear in this
     region", and South Africa is not on Shopify's list, so it returned false
     for every visitor and the card never showed. ToyDrop asks anyway, as a
     POPIA courtesy, and the answer is still honoured through the API.

     It still needs the API to load: without it the choice could not control
     anything, and a card whose buttons do nothing is worse than no card. */
  /* TWO FAULTS FIXED HERE, both real:

     1. THE LOCAL FLAG USED TO RUN BEFORE THE API. `localStorage` was read and
        returned on at the very top, so a stale flag suppressed the card even
        when Shopify's own state said no answer had been recorded, and a
        shopper whose consent had been reset could never be asked again. The
        API is now the authority and the flag is only a tiebreak, consulted
        last.

     2. THERE WAS NO WAY BACK. Once the flag was set the choice could never be
        revisited: no privacyBanner.showPreferences, no "manage cookies" link,
        nothing. That is the actual compliance gap on this surface, not the
        card failing to appear. The footer link wired below re-opens the card
        REGARDLESS of shouldShowBanner(), because "may I change my mind" is a
        different question from "is a prompt required in this region".

     Note for whoever debugs this next: the card is gated only on "has this
     shopper already chosen" (API state, then our flag) and on the API
     loading at all. Region is not a factor. */
  function initConsent() {
    var card = $('[data-td-consent]');
    var reopen = $('[data-consent-reopen]');
    if (!card) return;

    var KEY = 'td-consent';
    function stored() {
      try {
        return localStorage.getItem(KEY);
      } catch (e) {
        /* storage blocked: the API state decides on its own */
        return null;
      }
    }
    function remember(value) {
      try {
        localStorage.setItem(KEY, value);
      } catch (e) {
        /* private mode: the API recorded it; the card just re-asks next visit */
      }
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error) return;
        var api = window.Shopify.customerPrivacy;
        if (!api) return;

        function decide(granted) {
          api.setTrackingConsent(
            { analytics: granted, marketing: granted, preferences: granted },
            function () {}
          );
          remember(granted ? 'accepted' : 'declined');
          card.hidden = true;
          if (reopen) reopen.hidden = false;
        }

        var accept = $('[data-consent-accept]', card);
        var decline = $('[data-consent-decline]', card);
        if (accept) {
          accept.addEventListener('click', function () {
            decide(true);
          });
        }
        if (decline) {
          decline.addEventListener('click', function () {
            decide(false);
          });
        }

        if (reopen) {
          reopen.hidden = false;
          reopen.addEventListener('click', function (e) {
            e.preventDefault();
            card.hidden = false;
            card.scrollIntoView({ block: 'nearest' });
            var first = $('[data-consent-accept]', card);
            if (first) first.focus();
          });
        }

        /* FIRST VISIT ONLY: stay hidden if a choice is already recorded,
           asking the API first and our own flag second. No region gate. */
        if (
          typeof api.getTrackingConsent === 'function' &&
          api.getTrackingConsent() !== 'no_interaction'
        ) {
          return;
        }
        if (stored()) return;

        card.hidden = false;
      }
    );
  }

  /* ---------------- 12. Boot ---------------- */

  function boot() {
    heroLoad();
    initTicker();
    initNavDrops();
    initReveals();
    initHighlights();
    initParallax();
    initGallery();
    initBundle();
    initHeroVideo();
    initTdVideos();
    initCountdown();
    initStars();
    initCountUps();
    initStepsPath();
    initDropPaths();
    initReviewSummary();
    initLightbox();
    initSort();
    initNewsletter();
    initWelcome();
    initConsent();
    initHeaderCondense();
    initWaDodge();
    initReviewsRail();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
